import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { HospitalStatus } from '@ics-referral-hub/shared-types';
import { from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// A tab left open across a deploy may have a stale in-memory bundle whose
// lazy 'auth' chunk no longer exists on the server, rejecting navigate()
// (ChunkLoadError). Fall back to a hard redirect so the user always lands
// on a fresh, correctly-served login page instead of being stuck.
function navigateToLogin(router: Router, queryParams: Record<string, string>): void {
  const search = new URLSearchParams(queryParams).toString();
  const loginUrl = `/auth/login${search ? `?${search}` : ''}`;
  router.navigate(['/auth/login'], { queryParams }).then((ok) => {
    if (!ok) window.location.href = loginUrl;
  }).catch(() => {
    window.location.href = loginUrl;
  });
}

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  console.log('Auth guard check:', {
    url: state.url,
    isAuthenticated: authService.isAuthenticated(),
    hasAccessToken: !!authService.accessToken(),
    loginStep: authService.loginStep(),
  });

  // If already authenticated, allow access; re-verify org status if banner may be stale
  if (authService.isAuthenticated()) {
    const orgStatus = authService.organizationHospitalStatus();
    if (
      orgStatus === HospitalStatus.INACTIVE ||
      orgStatus === HospitalStatus.PENDING
    ) {
      authService.reconcileOrganizationHospitalStatus();
    }
    console.log('Auth guard: User is authenticated, allowing access');
    return true;
  }

  console.log('Auth guard: User not authenticated, checking startup auth...');

  // If not authenticated, try to refresh token using startup check
  return from(authService.checkAuthOnStartup()).pipe(
    map((isAuthenticated) => {
      console.log('Auth guard: Startup check result:', isAuthenticated);
      if (isAuthenticated) {
        console.log('Auth guard: Authentication restored, allowing access');
        return true;
      }

      // No valid refresh token, redirect to login with context if available
      console.log('Auth guard: No valid session found, redirecting to login');
      const failureReason = authService.consumeStartupFailureReason();

      const queryParams: Record<string, string> = { returnUrl: state.url };

      switch (failureReason) {
        case 'inactivity':
          queryParams['error'] = 'session_expired';
          queryParams['message'] =
            'You have been logged out due to inactivity. For security reasons, please log in again to continue.';
          break;
        case 'token_expired':
          queryParams['error'] = 'token_expired';
          queryParams['message'] =
            'Your session has expired. For your security, please log in again to continue.';
          break;
        case 'security':
          queryParams['error'] = 'security';
          queryParams['message'] =
            'For security reasons, you have been logged out. Please log in again to continue.';
          break;
        default:
          break;
      }

      navigateToLogin(router, queryParams);
      return false;
    }),
    catchError((error) => {
      // Error during token refresh, redirect to login
      console.log('Auth guard: Error during startup check:', error);

      // Don't show inactivity message for token refresh errors - only show generic login
      navigateToLogin(router, { returnUrl: state.url });
      return of(false);
    }),
  );
};
